import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { OrderMessageAttachment } from '../models/orders/order.types';
import {
  JuanaCards,
  JuanaCardsEvent,
  JuanaChatMessage,
  JuanaConversation,
  JuanaConversationSummary,
  JuanaMessageEvent,
  JuanaRole,
  JuanaTokenEvent,
} from '../models/juana/juana.types';
import { LanguageService } from './language.service';

interface HistoryMessage {
  role: string;
  content?: string;
  cards?: JuanaCards;
}

/** Payload del socket `order-message` (chat del pedido corp↔aliado). */
export interface OrderMessageEvent {
  orderId: string;
  senderRole: 'corporate' | 'provider' | 'admin';
  senderUserId?: string;
  text?: string;
  attachments?: OrderMessageAttachment[];
  createdAt?: string;
}

@Injectable({ providedIn: 'root' })
export class JuanaChatService {
  private readonly baseUrl = environment.apiUrl;
  private readonly CONV_ID_KEY = 'juana_conv_id';
  private socket?: Socket;
  private conversationId: string | null = null;
  private initializing = false;
  /** Red de seguridad: si la respuesta nunca llega (socket caído), no deja el input bloqueado para siempre. */
  private busyWatchdog?: ReturnType<typeof setTimeout>;
  private static readonly BUSY_WATCHDOG_MS = 60_000;

  // Estado del chat (signals, sobrevive cambios de ruta porque es root).
  readonly messages = signal<JuanaChatMessage[]>([]);
  /** Historial de conversaciones del usuario (GET /juana/conversations). */
  readonly conversations = signal<JuanaConversationSummary[]>([]);
  /** Id de la conversación abierta, para resaltarla en el historial. */
  readonly activeConversationId = signal<string | null>(null);
  readonly thinking = signal(false);
  /**
   * true desde que se envía un mensaje hasta que la respuesta de Juana queda
   * completa (juana-message) o falla. A diferencia de `thinking` (que se
   * apaga apenas llega el primer token, para que se vea "escribiendo..."),
   * `busy` cubre el turno completo — se usa para bloquear el input y que
   * solo se pueda mandar un mensaje a la vez (el backend puede rechazar un
   * segundo mensaje si llega muy pegado al anterior).
   */
  readonly busy = signal(false);
  readonly connected = signal(false);
  /** Mensajes del chat del pedido (corporativo↔taller) llegados por socket. */
  readonly orderMessages = signal<OrderMessageEvent[]>([]);
  /** Cambios de estado del pedido llegados por socket (ally acepta/confirma). */
  readonly orderStatusChanges = signal<Array<{ orderId: string; status: string }>>([]);

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
  ) {}

  /** Id de la conversación activa (para enlazar órdenes creadas desde una oferta). */
  get conversationIdValue(): string | null {
    return this.conversationId;
  }

  /** Inserta una tarjeta (p. ej. el link de pago de una orden) en el chat. */
  pushOrderCard(card: JuanaCards): void {
    this.messages.update(m => [...m, { role: 'tool', cards: card }]);
  }

  /** Inserta un aviso del asistente (p. ej. un error al crear la orden). */
  pushAssistantNotice(content: string): void {
    this.messages.update(m => [...m, { role: 'assistant', content, streaming: false }]);
  }

  /** Crea o reanuda la conversación y conecta el socket. */
  async init(): Promise<void> {
    if (this.conversationId || this.initializing) return;
    this.initializing = true;
    try {
      const savedId = sessionStorage.getItem(this.CONV_ID_KEY);
      if (savedId) {
        const resumedId = await this.resumeConversation(savedId);
        if (resumedId) return;
        sessionStorage.removeItem(this.CONV_ID_KEY);
      }
      await this.createConversation();
    } finally {
      this.initializing = false;
    }
  }

  /** Trae el historial de conversaciones del usuario (las más recientes primero). */
  async loadConversations(): Promise<void> {
    try {
      const list = await this.http
        .get<JuanaConversationSummary[]>(`${this.baseUrl}juana/conversations`, {
          withCredentials: true,
        })
        .toPromise();
      this.conversations.set(list ?? []);
    } catch {
      // El historial es accesorio: si falla, el chat sigue funcionando.
      this.conversations.set([]);
    }
  }

  /**
   * Abre una conversación anterior. El socket NO se reconecta: es por usuario,
   * no por conversación, y los eventos ya se filtran por conversationId — basta
   * con mover el id activo.
   */
  async openConversation(conversationId: string): Promise<boolean> {
    if (conversationId === this.conversationId) return true;

    this.messages.set([]);
    this.thinking.set(false);
    this.setBusy(false);

    const resumedId = await this.resumeConversation(conversationId);
    if (!resumedId) {
      this.messages.set([]);
      return false;
    }
    sessionStorage.setItem(this.CONV_ID_KEY, resumedId);
    this.activeConversationId.set(resumedId);
    return true;
  }

  /**
   * Renombra una conversación del historial. El backend marca el título como
   * editado a mano para que la segmentación automática no lo vuelva a pisar.
   */
  async renameConversation(conversationId: string, title: string): Promise<boolean> {
    try {
      await this.http
        .patch(
          `${this.baseUrl}juana/conversations/${conversationId}`,
          { title },
          { withCredentials: true },
        )
        .toPromise();
      await this.loadConversations();
      return true;
    } catch {
      return false;
    }
  }

  /** Descarta la conversación actual e inicia una nueva. */
  async newConversation(): Promise<void> {
    sessionStorage.removeItem(this.CONV_ID_KEY);
    this.conversationId = null;
    this.messages.set([]);
    this.thinking.set(false);
    this.setBusy(false);
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
      this.connected.set(false);
    }
    await this.createConversation();
  }

  private async createConversation(): Promise<void> {
    const geo = await this.getLocation();
    const conv = await this.http
      .post<JuanaConversation>(
        `${this.baseUrl}juana/conversations`,
        geo ?? {},
        { withCredentials: true },
      )
      .toPromise();
    this.conversationId = conv?.id ?? null;
    this.activeConversationId.set(this.conversationId);
    if (this.conversationId) {
      sessionStorage.setItem(this.CONV_ID_KEY, this.conversationId);
    }
    const tk = await this.http
      .get<{ token: string }>(`${this.baseUrl}juana/socket-token`, { withCredentials: true })
      .toPromise();
    this.connectSocket(tk?.token ?? '');
  }

  /**
   * Carga el historial de una conversación existente y reconecta el socket.
   * Devuelve true si la conversación existe y se restauró correctamente.
   */
  private async resumeConversation(conversationId: string): Promise<string | null> {
    try {
      // El id guardado puede apuntar a un hilo que el backend ya partió en
      // conversaciones temáticas y archivó. Sin este paso el usuario seguiría
      // escribiendo en un hilo vacío que ya no aparece en su historial, sin
      // ninguna señal de que pasó algo. `resolve` devuelve el id vigente.
      const resolved = await this.http
        .get<{ id: string }>(
          `${this.baseUrl}juana/conversations/${conversationId}/resolve`,
          { withCredentials: true },
        )
        .toPromise();
      if (resolved?.id) conversationId = resolved.id;

      const history = await this.http
        .get<HistoryMessage[]>(
          `${this.baseUrl}juana/conversations/${conversationId}/messages`,
          { withCredentials: true },
        )
        .toPromise();
      if (!history) return null;
      this.conversationId = conversationId;
      this.activeConversationId.set(conversationId);
      sessionStorage.setItem(this.CONV_ID_KEY, conversationId);

      this.messages.set(
        history
          .filter(m => m.role === 'user' || m.role === 'assistant' || m.role === 'tool')
          .map(m => ({
            role: m.role as JuanaRole,
            content: m.content,
            cards: m.cards,
            streaming: false,
          })),
      );
      const tk = await this.http
        .get<{ token: string }>(`${this.baseUrl}juana/socket-token`, { withCredentials: true })
        .toPromise();
      this.connectSocket(tk?.token ?? '');
      return conversationId;
    } catch {
      return null;
    }
  }

  /** Pide la ubicación del navegador; null si no hay permiso/soporte. */
  private getLocation(): Promise<{ lat: number; lng: number } | null> {
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 600000 },
      );
    });
  }

  private connectSocket(token: string) {
    if (this.socket) return;
    this.socket = io(environment.socketsUrl, {
      path: environment.socketsPath || undefined,
      transports: ['websocket'],
      auth: { token },
    });

    this.socket.on('connect', () => {
      this.connected.set(true);
    });
    this.socket.on('disconnect', () => this.connected.set(false));

    this.socket.on('juana-token', (e: JuanaTokenEvent) => {
      if (e.conversationId !== this.conversationId) return;
      this.appendToken(e.delta);
    });
    this.socket.on('juana-message', (e: JuanaMessageEvent) => {
      if (e.conversationId !== this.conversationId) return;
      this.finalizeAssistant(e.content);
    });
    this.socket.on('juana-cards', (e: JuanaCardsEvent) => {
      // Las ofertas llegan de forma proactiva (cuando un taller responde),
      // posiblemente fuera de la conversación abierta: app-sockets ya las
      // enruta por userId, así que las aceptamos aunque no coincida el id.
      const proactive = e.cards?.kind === 'availability-offer';
      if (!proactive && e.conversationId !== this.conversationId) return;
      this.messages.update(m => [...m, { role: 'tool', cards: e.cards }]);
    });
    // Chat del pedido (corporativo↔taller): enrutado por userId desde pitts-api.
    this.socket.on('order-message', (e: OrderMessageEvent) => {
      this.orderMessages.update(m => [...m, e]);
    });
    // Cambio de estado del pedido (aliado acepta / confirma envío).
    this.socket.on('order-status-changed', (e: { orderId: string; status: string }) => {
      this.orderStatusChanges.update(arr => [...arr, e]);
    });
  }

  /** Envía un mensaje del usuario. La respuesta llega por socket. */
  async send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Un mensaje a la vez: mientras no llegue la respuesta completa del
    // anterior (o falle), no se acepta uno nuevo — evita el "estoy
    // procesando tu mensaje anterior" si el usuario escribe muy rápido.
    if (this.busy()) return;
    if (!this.conversationId) {
      console.error('[Juana] No hay conversationId — reintentando init()');
      try { await this.init(); } catch (e) { console.error('[Juana] init() falló:', e); }
      if (!this.conversationId) {
        this.pushAssistantNotice('No se pudo conectar con Juana. Por favor recarga la página.');
        return;
      }
    }
    this.messages.update(m => [...m, { role: 'user', content: trimmed }]);
    this.thinking.set(true);
    this.setBusy(true);
    try {
      await this.http
        .post(
          `${this.baseUrl}juana/conversations/${this.conversationId}/messages`,
          { text: trimmed, lang: this.languageService.lang() },
          { withCredentials: true },
        )
        .toPromise();
      void this.loadConversations();
    } catch (e) {
      console.error('[Juana] send() falló:', e);
      this.thinking.set(false);
      this.setBusy(false);
      this.pushAssistantNotice('Error al enviar el mensaje. Por favor intenta de nuevo.');
    }
  }

  /** Centraliza el signal `busy` + su watchdog (nunca deja el input bloqueado si el socket se cae). */
  private setBusy(value: boolean): void {
    this.busy.set(value);
    clearTimeout(this.busyWatchdog);
    if (value) {
      this.busyWatchdog = setTimeout(() => this.busy.set(false), JuanaChatService.BUSY_WATCHDOG_MS);
    }
  }

  private appendToken(delta: string) {
    this.thinking.set(false);
    this.messages.update(m => {
      const last = m[m.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        const updated = { ...last, content: (last.content ?? '') + delta };
        return [...m.slice(0, -1), updated];
      }
      return [...m, { role: 'assistant', content: delta, streaming: true }];
    });
  }

  private finalizeAssistant(content: string) {
    this.thinking.set(false);
    this.setBusy(false);
    this.messages.update(m => {
      const last = m[m.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        return [...m.slice(0, -1), { role: 'assistant', content, streaming: false }];
      }
      return [...m, { role: 'assistant', content, streaming: false }];
    });
  }
}
